import { createApi, fetchBaseQuery, retry, BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { apiEndpoints, TRACKED_LINK_CONFIRMATION_ENDPOINT } from '@/lib/api/endpoints'
import { trackApiMetrics } from '@/lib/api/metrics'
import { getRequestOrigin } from '@/lib/getRequestOrigin'
import { appPath, stripBasePath } from '@/lib/getPublicAppUrl'
import { sessionExpiredPath } from '@/lib/auth/sessionExpiredPath'
import { track500Error } from '@/lib/api/track500'
import { reportErrorToDD } from '@/lib/analytics/datadogError'

const baseFetchQuery = fetchBaseQuery({
    baseUrl: getRequestOrigin(),
    prepareHeaders: (headers) => {
        headers.set('Cache-Control', 'no-cache, no-store, must-revalidate')
        return headers
    },
    credentials: 'include',
})

const normalizeRequestPath = (url: string): string => {
    if (url.startsWith('http')) {
        return new URL(url).pathname
    } else if (url.startsWith('/')) {
        return new URL(url, 'https://fake.com').pathname
    } else {
        return new URL(`/${url}`, 'https://fake.com').pathname
    }
}

let redirectingTo401Login = false

export const clientBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
    const startTime = Date.now()
    const requestMethod = typeof args === 'string' ? 'GET' : args.method || 'GET'
    const url = typeof args === 'string' ? args : args.url
    const requestPath = normalizeRequestPath(url)

    const result = await baseFetchQuery(args, api, extraOptions)

    let responseStatus = 0
    let errorName = 'SUCCESS'

    if (result?.error) {
        // RTK Query returns originalStatus for parsing errors (when response isn't valid JSON)
        const httpStatus =
            typeof result.error.status === 'number' ? result.error.status : (result.error as any).originalStatus

        // Login bounce is skipped only for this mutation on this path. Other 401s still redirect.
        if (
            httpStatus === 401 &&
            typeof window !== 'undefined' &&
            !redirectingTo401Login &&
            !(
                api.endpoint === TRACKED_LINK_CONFIRMATION_ENDPOINT &&
                requestPath === apiEndpoints.trackedLinks.confirm
            )
        ) {
            redirectingTo401Login = true
            window.location.href = appPath(
                sessionExpiredPath(
                    `${stripBasePath(window.location.pathname)}${window.location.search}`,
                ),
            )
            return result
        }

        if (typeof httpStatus === 'number') {
            responseStatus = httpStatus
            if (httpStatus >= 300 && httpStatus < 500) {
                errorName = 'ERR_BAD_REQUEST'
            } else {
                errorName = 'ERR_BAD_RESPONSE'
            }

            // Track 500 errors
            if (httpStatus >= 500) {
                track500Error({
                    endpoint: requestPath,
                    method: requestMethod,
                    status: httpStatus,
                    errorData: result.error.data,
                })
            }
        } else {
            // A non-number status indicates a network error.  Set responseStatus to 0 to
            // indicate that, and the status string will contain a more descriptive name of the error.
            responseStatus = 0
            errorName = String(result?.error?.status) || 'ERR_UNKNOWN'
        }
    } else {
        responseStatus = result?.meta?.response?.status || 200
        errorName = 'SUCCESS'
    }

    // Report API errors to DD RUM for Error Tracking correlation
    if (result?.error && (responseStatus >= 500 || responseStatus === 0)) {
        reportErrorToDD(new Error(`API ${requestMethod} ${requestPath} failed: ${errorName} (${responseStatus})`), {
            source: 'baseQuery',
            method: requestMethod,
            endpoint: requestPath,
            status: responseStatus,
            errorName,
        })
    }

    trackApiMetrics({
        requestMethod: requestMethod,
        requestPath: requestPath,
        responseStatus: responseStatus,
        errorName: errorName,
        latencyMs: Date.now() - startTime,
    })

    return result
}

const clientBaseQueryWithRetry = retry(clientBaseQuery, { maxRetries: 0 })

export const baseApi = createApi({
    reducerPath: 'api',
    baseQuery: clientBaseQueryWithRetry,
    tagTypes: [
        'User',
        'Dashboard',
        'Prescription',
        'Channel',
        'ChatHistory',
        'Sku',
        'SearchSuggestions',
        'HealthHistory',
        'EmergencyContacts',
        'PersonalInformation',
        'VisitCheckInSteps',
        'VisitCart',
        'Payment',
        'Appointment',
        'Provider',
        'InsuranceOnboarding',
        'InsuranceFlow',
        'PaymentMethods',
        'MercyBilling',
        'Insurance',
        'Specialty',
        'Referral',
        'CareJourney',
        'Order',
        'PharmacyOrder',
        'PharmacySearch',
        'PharmacyDetail',
        'PreferredPharmacy',
        'PharmacyCashPrice',
        'PharmacyInsurancePrice',
        'MedicationOptionCashPrice',
        'PharmacyComparison',
        'DirectoryPharmacy',
        'PharmacyMedications',
        'ProductDetailPage',
        'ReferralOrder',
        'ReferralOrderPreference',
        'ReferralOrderProvider',
        'ReferralOrderPayment',
        'PartnerConsents',
        'RequiredConsents',
        'CareJourney',
        'MedicationCheckInLabUpload',
        'FileUpload',
        'CarePlan',
        'CarePlanConditionGroups',
        'Updates',
        'UpdatesUnreadCount',
        'MessageCenter',
        'MyCare',
        'UrgentCareOrder',
        'NextStepCards',
        'PreVisitRequirements',
        'DashboardIntro',
        'SsoStatus',
        'Passkeys',
        'SignInMethodEnrollment',
        'UrgentCareOrderLocations',
        'PatientRegistrationStatus',
    ],
    endpoints: () => ({}),
})
